import { nanoid } from 'nanoid'
import { isEqual, cloneDeep } from 'lodash'
import { makeAutoObservable, reaction, type IReactionDisposer } from 'mobx'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { SegmentBox } from 'entities/Segment'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type Segment } from 'entities/Segment/model/Segment'
import {
  EnumEnrollmentMode,
  type IApplyChangeModal,
  type IEnrollmentInitProps,
  type IEnrollmentModalConfig,
} from 'widgets/ContactEnrollment/store/types'
import { FilterEditorStore, type IFilterStoreProps } from 'widgets/FilterEditor'
import { EnrollmentItemType, type IEnrollmentItem } from 'widgets/ContactEnrollment/types/types'
import type { ISearchDropdownStoreConfig } from 'widgets/SearchDropdown/store/types'
import { type EnrollmentFilters } from '../models'
import { type EnrollmentPreviewStore } from './enrollmentPreview'
import { EnrollmentListStore } from './enrollmentList'
import { EnrollmentModal } from '../ui/EnrollmentModal'

type IEnrollmentStoreConfig = {
  filterConfig: IFilterStoreProps
  searchDropdownConfig?: ISearchDropdownStoreConfig
  excludeSearchDropdownConfig?: ISearchDropdownStoreConfig
}

export const RE_ENROLL_CONTACTS_FILTER_KEYS = ['message_received_body', 'inbound_message_inbox']

export class EnrollmentStore {
  private _modalId: string = nanoid()
  private _modalApplyChangeId: string = nanoid()
  private readonly _enrollmentListStore: EnrollmentListStore
  private _mode: EnumEnrollmentMode = EnumEnrollmentMode.Edit
  private _segmentBox = new SegmentBox()
  private _excludeSegmentBox = new SegmentBox()
  private _disposerHasChangesSegment: IReactionDisposer | null = null
  private _disposerHasChangesExcludeSegment: IReactionDisposer | null = null
  private readonly _filterEditorStore: FilterEditorStore
  private readonly _excludeFilterEditorStore: FilterEditorStore
  private _isContactReEnroll = false
  private _isShowContactsAlert = false

  constructor(
    { filterConfig, searchDropdownConfig, excludeSearchDropdownConfig }: IEnrollmentStoreConfig,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)

    this._enrollmentListStore = new EnrollmentListStore(this._entities, {
      searchDropdownConfig: searchDropdownConfig,
      excludeSearchDropdownConfig: excludeSearchDropdownConfig,
    })
    this._filterEditorStore = new FilterEditorStore(
      {
        ...filterConfig,
        showSpawned: true,
        mutateFilterGroups: (groups, filterGroups) => filterGroups,
      },
      this._entities
    )
    this._excludeFilterEditorStore = new FilterEditorStore(
      {
        ...filterConfig,
        showSpawned: true,
        mutateFilterGroups: (groups, filterGroups) => filterGroups,
      },
      this._entities
    )
  }

  private reactionHasChangesSegmentFor = (
    segmentBox: SegmentBox,
    filterEditorStore: FilterEditorStore
  ) =>
    reaction(
      () => filterEditorStore.groups.toSegmentFilters(),
      (value) => {
        if (segmentBox.segment) {
          const filtersParams = cloneDeep(segmentBox.segment.filtersParams)

          segmentBox.setHasChanges(!isEqual(value, filtersParams))
        } else {
          const countWithValue = !!filterEditorStore.groups.countWithValue
          const countWithOutValue = !!filterEditorStore.groups.countWithOutValue

          if (countWithOutValue) return segmentBox.setHasChanges(false)

          segmentBox.setHasChanges(countWithValue)
        }
      }
    )

  initReactions = () => {
    this._disposerHasChangesSegment?.()
    this._disposerHasChangesSegment = this.reactionHasChangesSegmentFor(
      this._segmentBox,
      this._filterEditorStore
    )
    this._disposerHasChangesExcludeSegment?.()
    this._disposerHasChangesExcludeSegment = this.reactionHasChangesSegmentFor(
      this._excludeSegmentBox,
      this._excludeFilterEditorStore
    )
  }

  initFilters = () =>
    Promise.all([
      this._filterEditorStore.initFilters(),
      this._excludeFilterEditorStore.initFilters(),
    ])

  initEnrollmentList = ({
    filters,
    excludeFilters,
    number_id,
    confirmEnrollment,
  }: IEnrollmentInitProps) => {
    this._enrollmentListStore.init(filters, number_id, confirmEnrollment, excludeFilters)
  }

  get isContactReEnroll() {
    return this._isContactReEnroll
  }

  get isShowContactsAlert() {
    return this._isShowContactsAlert
  }

  setIsContactReEnroll = (value: boolean) => {
    this._isContactReEnroll = value
  }

  setIsShowContactsAlert = (value: boolean) => {
    this._isShowContactsAlert = value
  }

  isReEnrollFiltersSelected = (list: Segment[]) => {
    return list?.some((filter) => {
      return filter.filtersList.some((filterGroup) => {
        return filterGroup.filters.some((filter) =>
          RE_ENROLL_CONTACTS_FILTER_KEYS.includes(filter.key)
        )
      })
    })
  }

  setContactReEnrollAlert = (list: Segment[]) => {
    const isAlertFilters = this.isReEnrollFiltersSelected(list)

    this.setIsShowContactsAlert(isAlertFilters && this._isContactReEnroll)
  }

  openModal = ({
    title,
    titleDividerContent,
    allowEmptyCount,
    showDisabledTooltip,
    showExcludeTab,
    filters,
    excludeFilters,
    action,
    number_id,
  }: IEnrollmentModalConfig) => {
    this.initEnrollmentList({ filters, excludeFilters, number_id })
    this.initReactions()

    const dispose = (isAllowed: boolean) => {
      if (!isAllowed) return

      this._disposerHasChangesSegment?.()
      this._disposerHasChangesExcludeSegment?.()
      this._enrollmentListStore.reset()
      this._segmentBox.removeSegment()
      this._excludeSegmentBox.removeSegment()
      this._filterEditorStore.dispose()
      this._excludeFilterEditorStore.dispose()

      modalStore.removeModal(this._modalId)
    }

    const onCancel = () => {
      action.cancel().then(dispose)
    }

    const confirmAction = () => {
      action
        .confirm({
          filters: filters,
          excludeFilters: this._enrollmentListStore.excludeFilters,
          total: this._enrollmentListStore.totalCount,
          skipped: this._enrollmentListStore.totalInvalidCount,
        })
        .then(dispose)
    }

    const onConfirm = () => {
      this.applyChangeModal(async () => {
        await this._enrollmentListStore.waitForCount()
        confirmAction()
      })
    }

    const onClose = () => {
      this.applyChangeModal(() => {
        dispose(true)
      }, true)
    }

    modalStore.addModal({
      id: this._modalId,
      title: title ?? 'Contact enrollment',
      titleDividerContent: titleDividerContent,
      ModalContent: EnrollmentModal,
      ModalContentProps: {
        onCancel,
        onConfirm,
        onClose,
        allowEmptyCount,
        showDisabledTooltip,
        showExcludeTab,
        enrollmentListStore: this._enrollmentListStore,
        filterEditorStore: this._filterEditorStore,
        segmentBox: this._segmentBox,
        excludeFilterEditorStore: this._excludeFilterEditorStore,
        excludeSegmentBox: this._excludeSegmentBox,
        applyChangeModal: this.applyChangeModal,
        applyExcludeChangeModal: this.applyExcludeChangeModal,
        mode: this.mode,
      },
      width: 1000,
      pureContent: true,
      onClose,
    })
  }

  private applyChangeModalFor = (
    segmentBox: SegmentBox,
    filterEditorStore: FilterEditorStore,
    filters: EnrollmentFilters,
    addOrUpdateItem: (item: IEnrollmentItem) => void,
    preview: EnrollmentPreviewStore,
    onCallback?: () => void | Promise<void>,
    notUseApplyCallback?: boolean
  ) => {
    const segmentHasChanges = segmentBox.hasChanges
    const originalSegment = segmentBox.segment

    if (!segmentHasChanges || !originalSegment) {
      onCallback?.()

      return
    }

    const index = filters.filters.findIndex((filter) => filter.id === originalSegment.id)
    const name = index === -1 ? filters.filters.length + 1 : index + 1
    this._modalApplyChangeId = nanoid()

    modalStore.addModal({
      id: this._modalApplyChangeId,
      type: ModalTypeList.WARNING,
      title: (
        <>
          Apply changes to <br /> “Filter {name}”?
        </>
      ),
      primaryAction: {
        text: 'Apply changes',
        onAction: () => {
          const updatedSegment = filterEditorStore.groups.toSegment({
            id: originalSegment.id,
            organization_id: originalSegment.organizationId,
          })

          addOrUpdateItem({
            type: EnrollmentItemType.Filter,
            payload: updatedSegment,
          })
          segmentBox.setHasChanges(false)
          preview.resetItemActive(segmentBox)
          modalStore.closeModal(this._modalApplyChangeId)
          !notUseApplyCallback && onCallback?.()
        },
      },
      secondaryAction: {
        text: 'Keep editing',
        onAction: () => {
          modalStore.closeModal(this._modalApplyChangeId)
        },
      },
      ariaLabel: 'ApplyChange',
    })
  }

  applyChangeModal: IApplyChangeModal = (onCallback, notUseApplyCallback) => {
    this.applyChangeModalFor(
      this._segmentBox,
      this._filterEditorStore,
      this._enrollmentListStore.filters,
      this._enrollmentListStore.addOrUpdateItem,
      this._enrollmentListStore.preview,
      onCallback,
      notUseApplyCallback
    )
  }

  applyExcludeChangeModal: IApplyChangeModal = (onCallback, notUseApplyCallback) => {
    this.applyChangeModalFor(
      this._excludeSegmentBox,
      this._excludeFilterEditorStore,
      this._enrollmentListStore.excludeFilters,
      this._enrollmentListStore.addOrUpdateExcludeItem,
      this._enrollmentListStore.excludePreview,
      onCallback,
      notUseApplyCallback
    )
  }

  setTitleModal = ({
    title,
    titleDividerContent,
  }: {
    title: string
    titleDividerContent: string
  }) => {
    const modal = modalStore.getModal(this._modalId)

    if (modal) {
      if ('title' in modal) {
        modal.title = title
      }
      if ('titleDividerContent' in modal) {
        modal.titleDividerContent = titleDividerContent
      }
    }
  }

  setMode = (mode: EnumEnrollmentMode) => {
    this._mode = mode
  }

  setLoading = (status: boolean) => {
    this._enrollmentListStore.setLoading(status)
  }

  get total() {
    return this._enrollmentListStore.totalCount
  }

  get skipped() {
    return this._enrollmentListStore.totalInvalidCount
  }

  get mode() {
    return this._mode
  }

  get excludeFilterEditorStore() {
    return this._excludeFilterEditorStore
  }

  get filterEditorStore() {
    return this._filterEditorStore
  }
}
